import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  FileText,
  Loader2,
  Users,
  Calendar,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPrograms = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    track: "",
    weeks: 8,
    days_per_week: 3,
    category: "principiante",
    description: "",
    weekly_structure: "",
  });

  const categoryOptions = [
    { value: "principiante", label: "Principiante", color: "#22C55E" },
    { value: "intermedio", label: "Intermedio", color: "#3B82F6" },
    { value: "avanzado", label: "Avanzado", color: "#F59E0B" },
    { value: "goat", label: "GOAT", color: "#D4AF37" },
  ];

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/programs`, {
        headers: getAuthHeaders(),
      });
      setPrograms(response.data);
    } catch (error) {
      console.error("Error fetching programs:", error);
      toast.error("Error al cargar programas");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      track: "",
      weeks: 8,
      days_per_week: 3,
      category: "principiante",
      description: "",
      weekly_structure: "",
    });
    setEditingProgram(null);
  };

  const openEditDialog = (program) => {
    setEditingProgram(program);
    setFormData({
      name: program.name,
      track: program.track,
      weeks: program.weeks,
      days_per_week: program.days_per_week,
      category: program.category || "principiante",
      description: program.description || "",
      weekly_structure: program.weekly_structure || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.track) {
      toast.error("Por favor completa los campos obligatorios");
      return;
    }

    setSaving(true);
    try {
      if (editingProgram) {
        await axios.put(
          `${API_URL}/api/admin/programs/${editingProgram.id}`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Programa actualizado");
      } else {
        await axios.post(
          `${API_URL}/api/admin/programs`,
          formData,
          { headers: getAuthHeaders() }
        );
        toast.success("Programa creado");
      }
      
      fetchPrograms();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving program:", error);
      toast.error("Error al guardar el programa");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (programId) => {
    if (!window.confirm("¿Estás seguro de eliminar este programa?")) return;
    
    setDeleting(programId);
    try {
      await axios.delete(`${API_URL}/api/admin/programs/${programId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Programa eliminado");
      fetchPrograms();
    } catch (error) {
      console.error("Error deleting program:", error);
      toast.error("Error al eliminar el programa");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-programs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-white">Programas</h1>
          <p className="text-muted-foreground mt-1">Gestiona los programas de entrenamiento</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold shadow-lg shadow-[#D4AF37]/20" data-testid="add-program-button">
              <Plus className="w-5 h-5 mr-2" />
              Agregar Programa
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#2A2A2A] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-heading text-xl">
                {editingProgram ? "Editar Programa" : "Nuevo Programa"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-white">Nombre *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                  placeholder="Ej: PRINCIPIANTE HOMBRES"
                  data-testid="program-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Categoría *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="program-category-select">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-[#1A1A1A]">
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Track *</Label>
                <Select
                  value={formData.track}
                  onValueChange={(value) => setFormData({ ...formData, track: value })}
                >
                  <SelectTrigger className="bg-[#1A1A1A] border-[#2A2A2A] text-white" data-testid="program-track-select">
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                    <SelectItem value="men" className="text-white hover:bg-[#1A1A1A]">
                      Hombres
                    </SelectItem>
                    <SelectItem value="women" className="text-white hover:bg-[#1A1A1A]">
                      Mujeres
                    </SelectItem>
                    <SelectItem value="unisex" className="text-white hover:bg-[#1A1A1A]">
                      Unisex
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Estructura Semanal</Label>
                <Input
                  value={formData.weekly_structure}
                  onChange={(e) => setFormData({ ...formData, weekly_structure: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                  placeholder="Ej: Push / Pull / Legs"
                  data-testid="program-structure-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-white">Descripción</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                  placeholder="Descripción breve del programa"
                  data-testid="program-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Semanas</Label>
                  <Input
                    type="number"
                    value={formData.weeks}
                    onChange={(e) => setFormData({ ...formData, weeks: parseInt(e.target.value) })}
                    className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                    min={1}
                    max={52}
                    data-testid="program-weeks-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Días/Semana</Label>
                  <Input
                    type="number"
                    value={formData.days_per_week}
                    onChange={(e) => setFormData({ ...formData, days_per_week: parseInt(e.target.value) })}
                    className="bg-[#1A1A1A] border-[#2A2A2A] focus:border-[#D4AF37] text-white"
                    min={1}
                    max={7}
                    data-testid="program-days-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-[#2A2A2A] text-white hover:bg-[#1A1A1A]"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B] font-bold"
                  data-testid="save-program-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    editingProgram ? "Actualizar" : "Crear"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Programs Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <div key={i} className="h-48 skeleton-loading rounded-2xl" />
          ))}
        </div>
      ) : programs.length === 0 ? (
        <Card className="bg-[#121212] border-[#2A2A2A]">
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No hay programas creados</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {programs.map((program) => {
            const categoryInfo = categoryOptions.find(c => c.value === program.category) || categoryOptions[0];
            
            return (
            <Card
              key={program.id}
              className={cn(
                "bg-card border transition-colors hover:border-[#D4AF37]/50",
                "border-[#2A2A2A]"
              )}
              data-testid={`program-card-${program.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Badge 
                      className="text-xs font-bold"
                      style={{ 
                        backgroundColor: `${categoryInfo.color}20`,
                        color: categoryInfo.color,
                        border: `1px solid ${categoryInfo.color}40`
                      }}
                    >
                      {categoryInfo.label.toUpperCase()}
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        program.track === "men" 
                          ? "border-blue-500/50 text-blue-400" 
                          : program.track === "women"
                          ? "border-pink-500/50 text-pink-400"
                          : "border-gray-500/50 text-gray-400"
                      )}
                    >
                      <Users className="w-3 h-3 mr-1" />
                      {program.track === "men" ? "HOMBRES" : program.track === "women" ? "MUJERES" : "UNISEX"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(program)}
                      className="hover:bg-[#1A1A1A]"
                      data-testid={`edit-program-${program.id}`}
                    >
                      <Pencil className="w-4 h-4 text-muted-foreground" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(program.id)}
                      disabled={deleting === program.id}
                      className="hover:bg-destructive/20"
                      data-testid={`delete-program-${program.id}`}
                    >
                      {deleting === program.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 text-destructive" />
                      )}
                    </Button>
                  </div>
                </div>

                <h3 className="text-xl font-heading font-bold text-white mb-2">
                  {program.name}
                </h3>
                
                {program.weekly_structure && (
                  <p className="text-sm text-[#D4AF37] mb-3">{program.weekly_structure}</p>
                )}
                
                {program.description && (
                  <p className="text-sm text-[#666] mb-4 line-clamp-2">{program.description}</p>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-xl bg-[#1A1A1A]">
                    <p className="text-2xl font-heading font-bold text-[#D4AF37]">{program.weeks}</p>
                    <p className="text-sm text-muted-foreground">Semanas</p>
                  </div>
                  <div className="p-3 rounded-xl bg-[#1A1A1A]">
                    <p className="text-2xl font-heading font-bold text-[#D4AF37]">{program.days_per_week}</p>
                    <p className="text-sm text-muted-foreground">Días/Semana</p>
                  </div>
                </div>

                {/* View Days Button */}
                <Button
                  variant="outline"
                  className="w-full mt-4 border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                  onClick={() => navigate(`/admin/workout-days?program=${program.id}`)}
                  data-testid={`view-days-${program.id}`}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Ver días de entrenamiento
                  <ArrowRight className="w-4 h-4 ml-auto" />
                </Button>
              </CardContent>
            </Card>
          )})}
        </div>
      )}
    </div>
  );
};

export default AdminPrograms;
